import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Menu,
    Home,
    LayoutTemplate,
    FileText,
    Settings,
    User,
    UserCircle,
    LogOut,
    ChevronDown,
    ChevronLeft,
    TrendingUp,
    Users,
    Puzzle,
    FolderOpen,
    Sparkles,
    Mail,
    Clock,
    Receipt,
    Download,
    ShieldCheck,
    Shield,
    Scale,
} from "lucide-react";
import BrandLogo from "./BrandLogo";
import { Avatar } from "./Avatar";
import NotificationBell from "./NotificationBell";
import CreditBalancePill from "./CreditBalancePill";
import StorageUsageRow from "./StorageUsageRow";
import { useFeatureFlags } from "../lib/feature_flags";
import { useInstallPrompt } from "../lib/install";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
    SheetTitle,
    SheetDescription,
} from "../components/ui/sheet";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "../components/ui/dropdown-menu";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { useAuth } from "../lib/auth";
import { getUnreadNotificationCount } from "../lib/api";

const TABS = [
    { key: "projects", label: "Projects", icon: Home, path: "/home", flag: "show_top_projects" },
    { key: "contacts", label: "Contacts", icon: UserCircle, path: "/contacts", flag: "show_top_contacts" },
    { key: "leads", label: "Leads", icon: TrendingUp, path: "/leads", flag: "show_top_leads" },
    { key: "payables", label: "Payables", icon: Receipt, path: "/payables", flag: "show_top_payables" },
    { key: "templates", label: "Templates", icon: LayoutTemplate, path: "/templates", flag: "show_top_templates" },
    { key: "reports", label: "Reports", icon: FileText, path: "/reports", flag: "show_top_reports" },
    { key: "email", label: "Email", icon: Mail, path: "/email", flag: "show_top_email" },
    // iter-138: Files replaces Plugins in the main nav. Plugins moved
    // into the superadmin user-menu (Account dropdown → "Plugins") so
    // it stays out of the everyday contractor toolbar. The Files
    // explorer is the day-to-day tool.
    { key: "files", label: "Files", icon: FolderOpen, path: "/files", flag: "show_top_files" },
    { key: "settings", label: "Settings", icon: Settings, path: "/account", flag: "show_top_settings" },
];

function firstName(user) {
    if (!user) return "Account";
    const n = (user.full_name || "").trim();
    if (n) return n.split(/\s+/)[0];
    return (user.email || "Account").split("@")[0];
}

export const TopNav = ({ active = "projects", onTabChange }) => {
    const [open, setOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [unread, setUnread] = useState(0);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const name = firstName(user);
    const isSuperadmin = user?.role === "superadmin" || user?.role === "reviewer";
    const flags = useFeatureFlags();
    const showSubscriptions = flags.show_subscriptions !== false;
    // iter-114: Admin → Display tab kill-switches. Every UI element
    // referenced below defaults to visible; the operator can hide
    // them individually. `visibleTabs` is used for both the desktop
    // top bar and the mobile hamburger sheet.
    const visibleTabs = TABS.filter((t) => flags[t.flag] !== false);
    const showNotifications = flags.show_notifications !== false;
    const showAccountItem = flags.show_dropdown_account !== false;
    const showWorkgroupItem = flags.show_dropdown_workgroup !== false;
    const showHourLogItem = flags.show_dropdown_hour_log !== false;
    const showTopPrivacy = flags.show_top_privacy !== false;
    const showTopTerms = flags.show_top_terms !== false;
    // iter-126: PWA install entry-point in the user dropdown. Shown only
    // when the app is NOT already installed AND we either captured a
    // BeforeInstallPromptEvent (Chrome/Edge/Android) OR the user is on
    // iOS (where the prompt is manual via Share → Add to Home Screen).
    const install = useInstallPrompt();

    // Poll unread count so the avatar badge stays live even when the bell is closed.
    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const n = await getUnreadNotificationCount();
                if (!cancelled) setUnread(n);
            } catch {
                /* silent */
            }
        };
        tick();
        const id = setInterval(tick, 30_000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, []);

    // Show a mobile back button on any page other than the main dashboard/home.
    const showMobileBack = !["/home", "/"].includes(location.pathname);

    const handleTab = (t) => {
        if (onTabChange) {
            const handled = onTabChange(t.key);
            // If the caller returned a truthy value it indicates they handled
            // routing themselves. Otherwise fall through to the default path.
            if (handled) return;
        }
        if (t.path) navigate(t.path);
    };

    const handleBack = () => {
        // Prefer browser history; fall back to /home if there's nothing behind us.
        if (window.history.length > 1) {
            navigate(-1);
        } else {
            navigate("/home");
        }
    };

    const handleLogout = () => {
        logout();
        navigate("/", { replace: true });
    };

    const goAccount = () => {
        setOpen(false);
        navigate("/account");
    };

    return (
        <header
            className="sticky top-0 z-40 w-full border-b border-[#2A3655] bg-[#0A1128]/90 backdrop-blur-xl pt-[env(safe-area-inset-top)]"
            data-testid="top-nav"
        >
            <div className="max-w-7xl mx-auto flex items-center justify-between gap-2 px-4 md:px-8 py-3">
                <div className="flex items-center gap-2 min-w-0 shrink">
                    {showMobileBack && (
                        <button
                            onClick={handleBack}
                            className="md:hidden w-10 h-10 rounded-md border border-[#2A3655] bg-[#1C253C] text-white flex items-center justify-center active:scale-95 transition"
                            data-testid="mobile-back-button"
                            aria-label="Go back"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                    )}
                    <button
                        onClick={() => navigate("/?home=1")}
                        className="flex items-center"
                        data-testid="nav-logo"
                        aria-label="Steeltooth marketing site"
                        title="Open marketing site"
                    >
                        <BrandLogo />
                    </button>
                </div>

                <nav
                    className="hidden md:flex items-center gap-0.5 lg:gap-1 xl:gap-2 min-w-0 flex-1 justify-center overflow-x-auto no-scrollbar"
                    data-testid="nav-tabs"
                >
                    {TABS.filter((t) => flags[t.flag] !== false).map((t) => {
                        const Icon = t.icon;
                        const isActive = active === t.key;
                        return (
                            <button
                                key={t.key}
                                onClick={() => handleTab(t)}
                                data-testid={`nav-link-${t.key}`}
                                title={t.label}
                                className={`shrink-0 flex items-center gap-1.5 px-2 lg:px-3 xl:px-4 py-2 text-sm font-medium rounded-sm transition-colors whitespace-nowrap ${
                                    isActive
                                        ? "text-white bg-[#1C253C] border-b-2 border-[#00A3FF]"
                                        : "text-slate-400 hover:text-white hover:bg-[#1C253C]/60"
                                }`}
                            >
                                <Icon className="w-4 h-4 shrink-0" />
                                {/* iter-138: hide labels below xl so the full
                                    strip fits on 13-15" laptops + iPad
                                    landscape without clipping. */}
                                <span className="hidden xl:inline">{t.label}</span>
                            </button>
                        );
                    })}
                </nav>

                <div className="flex items-center gap-2 shrink-0">
                    {/* Credit balance pill (desktop only) */}
                    <CreditBalancePill />

                    {/* Notifications bell + invite modal */}
                    {showNotifications && <NotificationBell />}

                    {/* Desktop / tablet profile chip → dropdown */}
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="relative flex items-center gap-2 bg-[#1C253C] hover:bg-[#223055] pl-1 pr-1 sm:pl-2.5 sm:pr-2.5 py-1 sm:py-1.5 rounded-full border border-[#2A3655] transition-colors"
                                data-testid="user-profile-chip"
                                aria-label="Account menu"
                            >
                                <Avatar user={user} className="w-8 h-8 sm:w-7 sm:h-7 text-xs" icon={!user?.picture} />
                                {unread > 0 && (
                                    <span
                                        data-testid="user-profile-unread-dot"
                                        className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-rose-500 border-2 border-[#0A1128]"
                                    />
                                )}
                                <span className="hidden sm:inline text-sm font-medium text-white pr-1">
                                    {name}
                                </span>
                                <ChevronDown className="hidden sm:inline w-3.5 h-3.5 text-slate-400" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="w-56 bg-[#0F1B3A] border border-[#2A3655] text-slate-100"
                            data-testid="user-profile-menu"
                        >
                            <DropdownMenuLabel className="text-slate-400 font-normal text-xs">
                                {user?.email}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-[#2A3655]" />
                            {showAccountItem && (
                                <DropdownMenuItem
                                    onClick={goAccount}
                                    className="cursor-pointer focus:bg-[#1C253C] focus:text-white"
                                    data-testid="menu-account"
                                >
                                    <UserCircle className="w-4 h-4 mr-2 text-[#00A3FF]" />
                                    Account
                                </DropdownMenuItem>
                            )}
                            {/* iter-120: Workgroup back in the dropdown — sits directly
                                underneath Account, opens the standalone /workgroup page. */}
                            {showWorkgroupItem && (
                                <DropdownMenuItem
                                    onClick={() => navigate("/workgroup")}
                                    className="cursor-pointer focus:bg-[#1C253C] focus:text-white"
                                    data-testid="menu-workgroup"
                                >
                                    <Users className="w-4 h-4 mr-2 text-[#00A3FF]" />
                                    Workgroup
                                </DropdownMenuItem>
                            )}
                            {/* iter-118: Workgroup nav item removed — workgroup
                                management now lives on the Account page. */}
                            {showHourLogItem && (
                                <DropdownMenuItem
                                    onClick={() => navigate("/hour-log")}
                                    className="cursor-pointer focus:bg-[#1C253C] focus:text-white"
                                    data-testid="menu-hour-log"
                                >
                                    <Clock className="w-4 h-4 mr-2 text-[#00A3FF]" />
                                    Hour log
                                </DropdownMenuItem>
                            )}
                            {/* iter-126: Install app entry — hidden once
                                the app is already running standalone.
                                On Chrome/Edge/Android fires the captured
                                BeforeInstallPromptEvent; on iOS shows a
                                short toast with the Share → Add to Home
                                Screen tip. */}
                            {!install.installed && install.canInstall && (
                                <DropdownMenuItem
                                    onClick={async () => {
                                        if (install.deferred) {
                                            await install.promptInstall();
                                        } else if (install.isIos) {
                                            // Best we can do on iOS Safari:
                                            // pop the alert since there's
                                            // no scriptable install.
                                            window.alert(
                                                "To install Steeltooth on iOS:\n\n" +
                                                "1. Tap the Share icon in Safari\n" +
                                                "2. Scroll down and tap “Add to Home Screen”\n" +
                                                "3. Tap Add",
                                            );
                                        }
                                    }}
                                    className="cursor-pointer focus:bg-[#1C253C] focus:text-white"
                                    data-testid="menu-install"
                                >
                                    <Download className="w-4 h-4 mr-2 text-[#00A3FF]" />
                                    Install app
                                </DropdownMenuItem>
                            )}
                            {showSubscriptions && (
                                <DropdownMenuItem
                                    onClick={() => navigate("/subscriptions")}
                                    className="cursor-pointer focus:bg-[#1C253C] focus:text-white"
                                    data-testid="menu-subscriptions"
                                >
                                    <Sparkles className="w-4 h-4 mr-2 text-[#00A3FF]" />
                                    Subscription
                                </DropdownMenuItem>
                            )}
                            {/* iter-146: Plugins entry removed from user
                                dropdown entirely — was persisting for
                                superadmins because the display-flag
                                default is undefined (not false). The
                                Plugins page still lives at `/plugins`
                                and is reachable from Admin. */}
                            {isSuperadmin && (
                                <DropdownMenuItem
                                    onClick={() => navigate("/admin")}
                                    className="cursor-pointer focus:bg-[#1C253C] focus:text-white"
                                    data-testid="menu-admin"
                                >
                                    <ShieldCheck className="w-4 h-4 mr-2 text-rose-400" />
                                    Admin
                                </DropdownMenuItem>
                            )}
                            {/* iter-114: Privacy Policy + TOS deliberately removed
                                from the user dropdown — they live in the main
                                hamburger/top-menu, no need to duplicate here. */}
                            <DropdownMenuSeparator className="bg-[#2A3655]" />
                            <DropdownMenuItem
                                onClick={handleLogout}
                                className="cursor-pointer text-red-300 focus:bg-red-500/10 focus:text-red-200"
                                data-testid="menu-sign-out"
                            >
                                <LogOut className="w-4 h-4 mr-2" />
                                Sign out
                            </DropdownMenuItem>
                            <StorageUsageRow visible={menuOpen} />
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* Mobile sheet */}
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                className="md:hidden w-10 h-10 rounded-md border border-[#2A3655] bg-[#1C253C] text-white flex items-center justify-center active:scale-95 transition"
                                data-testid="mobile-menu-toggle"
                                aria-label="Open menu"
                            >
                                <Menu className="w-5 h-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="bg-[#0A1128] border-l border-[#2A3655] text-white flex flex-col"
                            data-testid="mobile-menu"
                        >
                            <VisuallyHidden.Root>
                                <SheetTitle>Navigation menu</SheetTitle>
                                <SheetDescription>
                                    Primary navigation for Steeltooth
                                </SheetDescription>
                            </VisuallyHidden.Root>
                            <div className="mt-2 mb-6">
                                <BrandLogo />
                            </div>
                            <nav className="flex flex-col gap-1">
                                {visibleTabs.map((t) => {
                                    const Icon = t.icon;
                                    const isActive = active === t.key;
                                    return (
                                        <button
                                            key={t.key}
                                            onClick={() => {
                                                handleTab(t);
                                                setOpen(false);
                                            }}
                                            data-testid={`mobile-nav-link-${t.key}`}
                                            className={`flex items-center gap-3 px-3 py-3 text-base rounded-sm transition-colors ${
                                                isActive
                                                    ? "bg-[#1C253C] text-white border-l-2 border-[#00A3FF]"
                                                    : "text-slate-300 hover:bg-[#1C253C]"
                                            }`}
                                        >
                                            <Icon className="w-5 h-5 text-[#00A3FF]" />
                                            <span>{t.label}</span>
                                        </button>
                                    );
                                })}
                                {showTopPrivacy && (
                                    <button
                                        onClick={() => {
                                            navigate("/privacy");
                                            setOpen(false);
                                        }}
                                        data-testid="mobile-nav-link-privacy"
                                        className="flex items-center gap-3 px-3 py-3 text-base rounded-sm transition-colors text-slate-300 hover:bg-[#1C253C] mt-4 border-t border-[#2A3655] pt-5"
                                    >
                                        <Shield className="w-5 h-5 text-[#00A3FF]" />
                                        <span>Privacy policy</span>
                                    </button>
                                )}
                                {showTopTerms && (
                                    <button
                                        onClick={() => {
                                            navigate("/terms");
                                            setOpen(false);
                                        }}
                                        data-testid="mobile-nav-link-terms"
                                        className="flex items-center gap-3 px-3 py-3 text-base rounded-sm transition-colors text-slate-300 hover:bg-[#1C253C]"
                                    >
                                        <Scale className="w-5 h-5 text-[#FFB020]" />
                                        <span>Terms of service</span>
                                    </button>
                                )}
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default TopNav;
